/**
 * GroveScore — the grove's music, tied to the fire rather than to the clock.
 *
 * `paintedSceneMusic` maps a whole region to one looping track, which is right
 * for a world you pass through and wrong for a game that happens in one
 * clearing all night. Here the score is two loops playing at once:
 *
 *   night   a full bed, loudest while the fire is strong
 *   embers  sparse and thin, rising as the fire falls
 *
 * They are crossfaded by the fire's fuel, so the music thins as the light does
 * without ever cutting or restarting. The player should never notice a
 * transition, only that the grove has got lonelier.
 *
 * `dawn` is a one-shot over the top when the night is survived.
 *
 * Two variants of each track were produced. TRACKS picks between them in one
 * place — swap the suffix to audition the other take.
 */
import * as Phaser from 'phaser';

const DIR = 'assets/audio/music';

export const GROVE_TRACKS = {
  night: { key: 'grove-night', url: `${DIR}/grove-night-1.mp3` },
  embers: { key: 'grove-embers', url: `${DIR}/grove-embers-1.mp3` },
  dawn: { key: 'grove-dawn', url: `${DIR}/grove-dawn-1.mp3` },
} as const;

/**
 * Ceiling for each bed. The score sits under the scene, not over it — this is
 * a game about listening to a wood at night, and the music is one voice in it
 * rather than the point. Halved from the first pass, which drowned the fire
 * crackle and the footsteps.
 */
const NIGHT_MAX = 0.08;
const EMBERS_MAX = 0.07;
const DAWN_MAX = 0.11;

/**
 * Fuel at which the beds are balanced. Above this the night bed dominates;
 * below it the embers bed takes over. Deliberately low — the thinning should
 * happen as the fire is genuinely failing, not the moment it stops being full.
 */
const CROSSFADE_PIVOT = 0.34;

/** Seconds for the mix to catch up. Slow enough to be felt, not heard. */
const FOLLOW_TAU = 2.6;

type Playable = Phaser.Sound.BaseSound & { setVolume(v: number): void };

export class GroveScore {
  private readonly scene: Phaser.Scene;
  private night: Playable | null = null;
  private embers: Playable | null = null;
  private dawn: Playable | null = null;

  private nightVol = 0;
  private embersVol = 0;
  private targetNight = 0;
  private targetEmbers = 0;
  private dawnPlayed = false;
  private destroyed = false;

  /** Preload every track. Call from the scene's preload(). */
  static preload(scene: Phaser.Scene): void {
    for (const t of Object.values(GROVE_TRACKS)) {
      if (!scene.cache.audio.exists(t.key)) scene.load.audio(t.key, t.url);
    }
  }

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
    this.night = this.add(GROVE_TRACKS.night.key, true);
    this.embers = this.add(GROVE_TRACKS.embers.key, true);
    // Start silent and let the first update() settle the mix, so the score
    // fades up rather than snapping on at whatever the fire happens to be.
    this.night?.play();
    this.embers?.play();
  }

  private add(key: string, loop: boolean): Playable | null {
    if (!this.scene.cache.audio.exists(key)) return null;
    return this.scene.sound.add(key, { loop, volume: 0 }) as Playable;
  }

  /**
   * @param fuel 0..1 — the fire's current fuel.
   * @param delta frame delta in ms.
   */
  update(fuel: number, delta: number): void {
    if (this.destroyed) return;

    // A smooth crossfade rather than a hard switch at the pivot: both beds are
    // audible through the middle, which is what keeps it from sounding cut.
    const t = Phaser.Math.Clamp((fuel - CROSSFADE_PIVOT) / 0.34 + 0.5, 0, 1);
    this.targetNight = NIGHT_MAX * t;
    this.targetEmbers = EMBERS_MAX * (1 - t);

    const k = 1 - Math.exp(-(delta / 1000) / FOLLOW_TAU);
    this.nightVol += (this.targetNight - this.nightVol) * k;
    this.embersVol += (this.targetEmbers - this.embersVol) * k;

    this.night?.setVolume(this.nightVol);
    this.embers?.setVolume(this.embersVol);
  }

  /** Fire the dawn cue once, and duck the night beds under it. */
  playDawn(): void {
    if (this.destroyed || this.dawnPlayed) return;
    this.dawnPlayed = true;
    this.dawn = this.add(GROVE_TRACKS.dawn.key, false);
    this.dawn?.play();
    if (this.dawn) this.dawn.setVolume(DAWN_MAX);
    this.scene.tweens.add({
      targets: this,
      nightVol: 0,
      embersVol: 0,
      duration: 4200,
      ease: 'Sine.easeInOut',
      onUpdate: () => {
        this.night?.setVolume(this.nightVol);
        this.embers?.setVolume(this.embersVol);
      },
    });
  }

  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;
    for (const s of [this.night, this.embers, this.dawn]) {
      if (!s) continue;
      s.stop();
      s.destroy();
    }
    this.night = null;
    this.embers = null;
    this.dawn = null;
  }
}
